import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

import { useTranslation } from "react-i18next";
import {
  UxAction,
  UxIconEnum,
  UxSizeEnum,
  UxIconLibraryEnum,
  UxEnhancedIcon,
  UxOutlinePriorityEnum
} from "@imaginelearning/mps-ux-component-library";
import { Button } from "react-bootstrap";
import useOnDemandTutoring from "ondemandtutoring/hooks/useondemandtutoring";
import useGoPeer, { UserData } from "../hooks/usegopeer";
import useGradeRange from "../../hooks/usegraderange";
import "../style/ondemandtutoring.scss";

interface IOnDemandTutoringButtonProps {
  user: UserData;
}

function OnDemandTutoringButton({ user }: IOnDemandTutoringButtonProps) {
  const [showPopover, setShowPopover] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const onDemandTutoring = useOnDemandTutoring();
  const { t } = useTranslation();
  const { GoPeerButton } = useGoPeer(user);
  const { gradeButtonStyle } = useGradeRange();

  const handleCancel = () => {
    setShowPopover(false);
    onDemandTutoring.closeNudge();
  };

  const handleMouseOver = () => {
    setShowTooltip(true);
  };

  const handleMouseOut = () => {
    setShowTooltip(false);
  };

  useLayoutEffect(() => {
    positionToolTipAndPopover();
    if (onDemandTutoring.showNudge) {
      setShowPopover(true);
    }
  }, [onDemandTutoring.showNudge]);

  const positionToolTipAndPopover = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipEl = document.querySelector(".ondemand-tooltip");
      const popoverEl = document.querySelector(".ondemand-popover");

      if (showTooltip && tooltipEl) {
        tooltipEl.style.left = `${buttonRect.left - 50}px`;
      }

      if (showPopover && popoverEl) {
        popoverEl.style.left = `${buttonRect.left - 50}px`;
      }
    }
  };

  useLayoutEffect(() => {
    positionToolTipAndPopover();
  }, [showTooltip, showPopover]);

  useEffect(() => {
    positionToolTipAndPopover();
  }, []);

  if (!onDemandTutoring.visible) {
    return null;
  }

  return (
    <>
      <Button
        variant="light"
        size="lg"
        ref={buttonRef}
        disabled={!onDemandTutoring.enabled}
        onClick={() => setShowPopover(!showPopover)}
        className="mpng-ux-action"
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        data-testid="on-demand-tutoring-button"
      >
        <UxEnhancedIcon
          icon={UxIconEnum.COMMENT_QUESTION as any}
          size={UxSizeEnum.LARGE as any}
          library={UxIconLibraryEnum.DUOTONE}
          className="onDemandTutoring"
        />
      </Button>

      <div className="ondemand-tooltip" style={{ display: showTooltip ? "block" : "none" }}>
        {onDemandTutoring.areTeachersAvailable ? t("onDemandEnabledText") : t("noTeachersAvailable")}
      </div>

      {showPopover && <div className="ondemand-background-overlay" />}

      {showPopover && ( // This is the popover that appears when the button is clicked
        <div className="ondemand-popover">
          <img id="odt-teacher" src="ondemandtutoringimage.png" alt="On-Demand Tutoring" />
          <h3>{onDemandTutoring.showNudge ? t("AskLiveTeacher") : t("NeedLiveHelp")}</h3>
          {onDemandTutoring.showNudge && <p>{t("AskLiveTeacherLong")}</p>}
          <p>{onDemandTutoring.areTeachersAvailable ? t("OpenInNewTab") : t("noTeachersAvailable")}</p>

          <UxAction
            key={0}
            enabled={true}
            text={t("Close")}
            priority={UxOutlinePriorityEnum.OUTLINE_PRIMARY}
            callback={handleCancel}
            style={gradeButtonStyle()}
            className="px-3 "
          />

          {onDemandTutoring.areTeachersAvailable && (
            <GoPeerButton text={t("Chat Now")} onClick={() => setShowPopover(false)} disabled={false} />
          )}
        </div>
      )}
    </>
  );
}

export default OnDemandTutoringButton;
